import { useEffect, useState } from 'react';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { formatoCantidad, formatoPorcentaje, formatoPrecio } from '@/lib/utils/formato-precio';
import { AppLayout } from '@/components/layout/app-layout';
import { Button, buttonVariants } from '@/components/ui/button';
import { PageHeader } from '@/components/patterns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Plus,
  Eye,
  Trash2,
  Search,
  Ship,
  Calculator,
  BarChart3,
  Filter,
  X,
  LayoutGrid,
  List,
  Globe,
  FileSpreadsheet,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { FadeIn } from '@/components/motion';
import { useToast } from '@/lib/hooks/use-toast';
import { ImportTaxConfigDialog } from '../components/import-tax-config-dialog';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { ImportStatusBadge } from '../components/import-status-badge';
import { TradeTypeBadge } from '../components/trade-type-badge';
import { StatusMiniProgress } from '../components/status-mini-progress';
import { ImportStatsCards } from '../components/import-stats-cards';
import { ImportPipelineBoard } from '../components/import-pipeline-board';
import { ImportOrderWizard } from '../components/import-order-wizard';
import { ContainerAvailabilityView } from '../components/container-availability-view';
import { importTrackingService } from '../services/import-tracking-service';
import { QuotesTable } from '@/features/trade/components/quotes-table';
import { RequoteDialog } from '@/features/trade/components/requote-dialog';
import { QuoteFunnel } from '@/features/trade/components/quote-funnel';
import type { QuoteOutcome } from '@/features/trade/utils/quote-outcome';
import type { ImportOrderStatus } from '../types/import-tracking.types';

// ── Status filter options ────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'DRAFT', label: 'Borrador' },
  { value: 'CONFIRMED', label: 'Confirmada' },
  { value: 'SHIPPED', label: 'Embarcada' },
  { value: 'IN_TRANSIT', label: 'En tránsito' },
  { value: 'CUSTOMS', label: 'En aduana' },
  { value: 'CUSTOMS_HOLD', label: 'Retenida en aduana' },
  { value: 'DELIVERED', label: 'Entregada' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

// ── Helpers ──────────────────────────────────────────────────────

function formatMoney(value: number | string | null | undefined, currency = 'USD'): string {
  const n = typeof value === 'number' ? value : value == null || value === '' ? 0 : Number(value);
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-BO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ── Page Component ───────────────────────────────────────────────

/**
 * Pestañas direccionables por `?tab=`.
 *
 * El menú lateral apunta a cada una directamente, así que los valores son
 * parte de la URL pública: cambiarlos rompe los enlaces guardados.
 */
const IMPORT_TABS = ['orders', 'quotes', 'availability', 'dashboard'];

/**
 * Cotizaciones por página.
 *
 * Veinticinco entran en una pantalla sin scroll infinito y dejan ver el tablero
 * de arriba al mismo tiempo. Con cincuenta hay que bajar para entender de qué
 * está hablando el resumen.
 */
const QUOTES_PER_PAGE = 25;

/**
 * Encabezado por sección.
 *
 * Estas cinco vistas comparten una sola página, y hasta ahora el título decía
 * "Import/Export" en todas: estabas en Cotizaciones y el título te decía que
 * estabas en otro lado. Cada sección se nombra por lo que es.
 */
const SECTIONS: Record<
  string,
  {
    icon: LucideIcon;
    title: string;
    description: string;
    showNewOrder?: boolean;
    showTaxConfig?: boolean;
  }
> = {
  orders: {
    icon: Ship,
    title: 'Import / Export',
    description: 'Los embarques en curso, del zarpe a la entrega',
    showNewOrder: true,
  },
  quotes: {
    icon: FileSpreadsheet,
    title: 'Cotizaciones',
    description: 'Precios enviados al cliente que todavía no son embarque',
  },
  availability: {
    icon: Globe,
    title: 'Contenedores',
    description: 'Qué hay disponible y dónde, por puerto',
  },
  dashboard: {
    icon: BarChart3,
    title: 'Indicadores',
    description: 'Órdenes por estado, tiempos promedio y conversión de cotizaciones',
  },
};

export interface ImportOrdersPageProps {
  /**
   * Qué sección mostrar. Viene de la ruta (`/trade/cotizaciones` → `quotes`).
   *
   * Antes salía de `?tab=`, y por eso todas las secciones compartían la misma
   * URL: el enlace no decía dónde estabas.
   */
  section?: string;
}

export function ImportOrdersPage({ section: sectionProp }: ImportOrdersPageProps = {}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    orders,
    ordersTotal,
    ordersPage,
    ordersTotalPages,
    ordersSearch,
    ordersStatusFilter,
    ordersSupplierFilter,
    ordersDateFrom,
    ordersDateTo,
    isLoadingOrders,
    setOrdersSearch,
    setOrdersStatusFilter,
    setOrdersSupplierFilter,
    setOrdersDateRange,
    fetchOrders,
    deleteOrder,
    // Dashboard
    dashboardStats,
    isLoadingDashboard,
    fetchDashboard,
    // Suppliers
    suppliers,
    fetchSuppliers,
  } = useImportTrackingStore();

  const { confirm, ConfirmDialog } = useConfirmDialog();

  // La sección la manda la ruta. `?tab=` se sigue leyendo solo para las URLs
  // viejas que todavía llegan por la redirección.
  const [searchParams, setParametros] = useSearchParams();
  const tabParam = sectionProp ?? searchParams.get('tab');
  const activeTab = tabParam && IMPORT_TABS.includes(tabParam) ? tabParam : 'orders';

  const section = SECTIONS[activeTab] ?? SECTIONS.orders;
  const SectionIcon = section.icon;

  /**
   * Base de la URL del detalle.
   *
   * Abrir una cotización desde Cotizaciones deja `/trade/cotizaciones/:id`, y
   * desde Embarques `/trade/embarques/:id`. Es la misma pantalla, pero la URL
   * dice de dónde venís — y era la queja concreta: al entrar a una cotización
   * quedabas marcado en Import/Export.
   */
  const detailBase = activeTab === 'quotes' ? '/trade/cotizaciones' : '/trade/embarques';

  const [viewMode, setViewMode] = useState<'table' | 'pipeline'>('pipeline');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  /*
    «Nueva importación» de la portada de Comercia llega con `?nueva=1`: se abre
    el asistente y se limpia la dirección, para que recargar no lo vuelva a abrir.
  */
  useEffect(() => {
    if (searchParams.get('nueva') !== '1' || !section.showNewOrder) return;
    setShowCreateDialog(true);
    setParametros(
      (prev) => {
        const sig = new URLSearchParams(prev);
        sig.delete('nueva');
        return sig;
      },
      { replace: true }
    );
  }, [searchParams, section.showNewOrder, setParametros]);
  const [showTaxConfigDialog, setShowTaxConfigDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Separate quotes list
  const [quotes, setQuotes] = useState<typeof orders>([]);
  const [quotesTotal, setQuotesTotal] = useState(0);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  /**
   * Paginación de cotizaciones.
   *
   * Antes se pedían las primeras 50 y punto. Con 51 el encabezado decía "51
   * cotizaciones guardadas" y la tabla mostraba 50: la que faltaba no daba
   * ningún error, simplemente no estaba. El peor síntoma posible, y con varias
   * cotizaciones por semana era cuestión de meses.
   */
  const [quotesPage, setQuotesPage] = useState(1);
  const [quotesTotalPages, setQuotesTotalPages] = useState(1);
  /**
   * `true` = mostrar también las que ya se volvieron embarque.
   *
   * Arranca ENCENDIDO. Estaba apagado con el argumento de que el trabajo del
   * día son las que esperan respuesta, pero el efecto era que "Cotizaciones"
   * no mostraba las cotizaciones: al convertir una y confirmarla desaparecía
   * de la lista, y el filtro de estado quedaba con dos opciones de cinco.
   *
   * Cotizaciones es el registro de todo lo que se cotizó. Para quedarse solo
   * con las que esperan respuesta está el filtro de estado, que ahora sí
   * ofrece los cinco.
   */
  const [quotesHistory, setQuotesHistory] = useState(true);
  /** Cotización que se está recotizando. `null` = el diálogo está cerrado. */
  const [requoteId, setRequoteId] = useState<string | null>(null);
  /**
   * Sube en cada cambio para que el embudo se vuelva a leer.
   *
   * Un contador y no el total de cotizaciones: registrar que una se perdió no
   * cambia cuántas hay, así que atado al total el embudo se quedaba mostrando
   * los números viejos justo después de la acción que los invalidó.
   */
  const [quotesVersion, setQuotesVersion] = useState(0);
  /**
   * El estado comercial por el que está filtrada la lista de cotizaciones.
   *
   * Vive acá y no dentro de la tabla porque lo comparten dos componentes: las
   * tarjetas del embudo lo escriben al hacerles clic, y la tabla lo lee.
   */
  const [filtroResultado, setFiltroResultado] = useState<QuoteOutcome | 'all'>('all');

  /**
   * Cotizaciones que esperan respuesta del cliente.
   *
   * Filtra por `status: 'QUOTE'` y NO por `isQuote`. Son cosas distintas:
   * `isQuote` marca el ORIGEN y queda en `true` para siempre, así que filtrar
   * por ahí traía también las cotizaciones ya convertidas en orden.
   */
  const fetchQuotes = async (opts?: { page?: number; history?: boolean }) => {
    const page = opts?.page ?? quotesPage;
    const history = opts?.history ?? quotesHistory;

    setIsLoadingQuotes(true);
    try {
      const data = await importTrackingService.getOrders({
        // `quote` = las que esperan respuesta. `quote-history` = todo lo
        // cotizado alguna vez, para poder ver cuáles se ganaron.
        stage: history ? 'quote-history' : 'quote',
        page,
        limit: QUOTES_PER_PAGE,
      });
      setQuotes(data.orders);
      setQuotesTotal(data.total);
      // El total de páginas lo manda el servidor: calcularlo acá duplicaría la
      // división y quedaría mal el día que el backend cambie el tamaño.
      setQuotesTotalPages(data.totalPages ?? 1);
      setQuotesPage(page);
    } catch (err) {
      console.error('[import-orders] fetchQuotes failed:', err);
    } finally {
      setIsLoadingQuotes(false);
    }
  };

  useEffect(() => {
    fetchOrders({ stage: 'operational' });
    fetchQuotes();
    fetchDashboard();
    fetchSuppliers();
  }, []);

  // Re-fetch when filters change
  useEffect(() => {
    fetchOrders({ page: 1, stage: 'operational' });
  }, [ordersStatusFilter, ordersSupplierFilter, ordersDateFrom, ordersDateTo]);

  // Re-fetch al tipear en el buscador (debounce 300ms para no spamear al backend)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchOrders({ page: 1, stage: 'operational' });
    }, 300);
    return () => clearTimeout(t);
  }, [ordersSearch]);

  const handleSearch = () => {
    fetchOrders({ page: 1, stage: 'operational' });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDelete = (id: string) => {
    confirm({
      title: 'Eliminar orden',
      description: 'Esta acción eliminará la orden y todos sus tramos. ¿Continuar?',
      variant: 'destructive',
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          await deleteOrder(id);
          toast({ title: 'Orden eliminada', variant: 'success' });
        } catch (err: any) {
          toast({ title: 'Error', description: err.message, variant: 'destructive' });
        }
      },
    });
  };

  /**
   * Duplicar: la misma configuración, para cambiarle algo.
   *
   * Mismo cliente, misma ruta y mismo equipo, pero **sin** `desde`: una
   * duplicada nace independiente. El encadenado se reserva para recotizar, que
   * es cuando de verdad hay una cotización que reemplaza a otra.
   *
   * Va por la URL del cotizador y no por un endpoint que copie la fila: quien
   * duplica casi siempre quiere cambiar algo, y llegar a una pantalla con todo
   * cargado y editable es más útil que una copia exacta que después hay que
   * corregir.
   */
  const duplicarCotizacion = (quote: (typeof quotes)[number]) => {
    const p = new URLSearchParams();
    if (quote.quotedContainerTypeId) p.set('equipo', quote.quotedContainerTypeId);
    if (quote.quotedWeightKg !== null) p.set('peso', String(quote.quotedWeightKg));
    p.set('cliente', quote.customerId ?? '');
    navigate(`/trade/cotizador?${p.toString()}`);
  };

  /**
   * El cliente aceptó: la cotización pasa a ser una orden.
   *
   * Se pide confirmación porque cambia el estado y libera las pestañas de
   * operación (entidades, contenedores). El backend valida que esté en QUOTE.
   */
  const handleConvertQuote = (quote: (typeof quotes)[number]) => {
    confirm({
      title: `Convertir ${quote.orderNumber} en orden`,
      description:
        'La cotización pasa a ser una orden de importación y se habilitan las pestañas de ' +
        'operación. El precio cotizado queda como está.',
      confirmText: 'Convertir',
      onConfirm: async () => {
        try {
          const orden = await importTrackingService.convertToOrder(quote.id);
          toast({
            title: 'Convertida en orden',
            description: `${quote.orderNumber} ya es una orden de importación.`,
            variant: 'success',
          });
          fetchQuotes();
          fetchOrders({ page: 1, stage: 'operational' });
          navigate(`/trade/embarques/${orden.id}`);
        } catch (err) {
          toast({
            title: 'No se pudo convertir',
            description:
              (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              'Ocurrió un error al convertir la cotización.',
            variant: 'destructive',
          });
        }
      },
    });
  };

  const handlePageChange = (page: number) => {
    fetchOrders({ page, stage: 'operational' });
  };

  const supplierOptions = [
    { value: '', label: 'Todos los proveedores' },
    ...suppliers.map((s) => ({ value: s.id, label: s.name })),
  ];

  const clearFilters = () => {
    setOrdersSearch('');
    setOrdersStatusFilter('');
    setOrdersSupplierFilter('');
    setOrdersDateRange('', '');
  };

  const hasActiveFilters = !!(
    ordersStatusFilter ||
    ordersSupplierFilter ||
    ordersDateFrom ||
    ordersDateTo
  );

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/*
          El encabezado de Comercia (artefacto, 19-oct): la píldora del módulo, el
          título y las acciones. Los botones son por sección: "Nueva orden" no
          tiene nada que hacer en Indicadores, y "Configurar tasas" solo importa
          donde se calculan tributos.
        */}
        <PageHeader
          modulo="comercia"
          etiqueta="Comercia"
          icono={SectionIcon}
          titulo={section.title}
          subtitulo={section.description}
          acciones={
            <>
              {section.showTaxConfig && (
                <Button variant="outline" onClick={() => setShowTaxConfigDialog(true)}>
                  <Settings className="h-4 w-4" aria-hidden="true" />
                  Configurar tasas
                </Button>
              )}
              {activeTab === 'quotes' && (
                <Link
                  to="/trade/cotizador"
                  className={buttonVariants({
                    variant: 'degradado',
                    size: 'lg',
                    className: 'px-5',
                  })}
                >
                  <Calculator className="h-4 w-4" aria-hidden="true" />
                  Cotizar un embarque
                </Link>
              )}
              {section.showNewOrder && (
                <Button
                  variant="degradado"
                  size="lg"
                  className="px-5"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Nueva orden
                </Button>
              )}
            </>
          }
        />

        {/*
          El tablero mide EMBARQUES en operación —activos, en tránsito, en
          aduana, entregados—, así que solo tiene sentido donde se ven embarques.

          En Cotizaciones aparecía en cero y encima de los números de
          cotizaciones: dos tableros seguidos, el primero irrelevante, nueve
          números antes de llegar a la tabla. En Contenedores pasaba lo mismo.
        */}
        {(activeTab === 'orders' || activeTab === 'dashboard') && (
          <FadeIn delay={0.1}>
            <ImportStatsCards stats={dashboardStats} isLoading={isLoadingDashboard} />
          </FadeIn>
        )}

        {/* Tabs */}
        <FadeIn delay={0.15}>
          <Tabs value={activeTab} onValueChange={() => undefined}>
            {/*
              La barra de pestañas se quitó: cada una de estas cinco vistas ya es
              una entrada del menú de Trade Intelligence. Tenerla acá también
              significaba dos navegaciones para lo mismo — y era la que hacía que
              al entrar a una cotización se marcara "Import/Export".

              El componente `Tabs` se mantiene porque la vista sigue saliendo de
              `?tab=`, pero quién manda es el menú.
            */}

            {/* Orders Tab */}
            <TabsContent value="orders">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Órdenes Import/Export</CardTitle>
                      <CardDescription>
                        {viewMode === 'pipeline'
                          ? // Sin sumarlas: una cotización convertida es también una orden y se contaba dos veces.
                            `${ordersTotal} órdenes · ${quotesTotal} cotizaciones`
                          : `${ordersTotal} órdenes en total`}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* View mode toggle */}
                      <div className="flex items-center border rounded-lg p-0.5 bg-muted/50">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={viewMode === 'pipeline' ? 'default' : 'ghost'}
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => setViewMode('pipeline')}
                            >
                              <LayoutGrid className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Vista Pipeline</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={viewMode === 'table' ? 'default' : 'ghost'}
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => setViewMode('table')}
                            >
                              <List className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Vista Lista</TooltipContent>
                        </Tooltip>
                      </div>

                      {/* Separator */}
                      <div className="h-6 w-px bg-border" />

                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          className="pl-9 w-64"
                          placeholder="Buscar por N° orden, proveedor..."
                          value={ordersSearch}
                          onChange={(e) => setOrdersSearch(e.target.value)}
                          onKeyDown={handleSearchKeyDown}
                        />
                      </div>
                      {/* Filter toggle */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant={showFilters ? 'default' : 'outline'}
                            size="icon"
                            onClick={() => setShowFilters(!showFilters)}
                          >
                            <Filter className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Filtros</TooltipContent>
                      </Tooltip>
                      {hasActiveFilters && (
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          <X className="w-4 h-4 mr-1" />
                          Limpiar
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expandable filters */}
                  {showFilters && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t mt-3">
                      <SearchableSelect
                        options={STATUS_OPTIONS}
                        value={ordersStatusFilter}
                        onValueChange={(v) => setOrdersStatusFilter(v as ImportOrderStatus | '')}
                        placeholder="Estado..."
                        searchPlaceholder="Buscar estado..."
                      />
                      <SearchableSelect
                        options={supplierOptions}
                        value={ordersSupplierFilter}
                        onValueChange={setOrdersSupplierFilter}
                        placeholder="Proveedor..."
                        searchPlaceholder="Buscar proveedor..."
                      />
                      <Input
                        type="date"
                        value={ordersDateFrom}
                        onChange={(e) => setOrdersDateRange(e.target.value, ordersDateTo)}
                        placeholder="Desde..."
                      />
                      <Input
                        type="date"
                        value={ordersDateTo}
                        onChange={(e) => setOrdersDateRange(ordersDateFrom, e.target.value)}
                        placeholder="Hasta..."
                      />
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {isLoadingOrders ? (
                    <div className="space-y-3">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-4">
                          <Skeleton variant="shimmer" className="h-5 w-24" />
                          <Skeleton variant="shimmer" className="h-5 w-32" />
                          <Skeleton variant="shimmer" className="h-5 w-20" />
                          <Skeleton variant="shimmer" className="h-5 w-24 ml-auto" />
                        </div>
                      ))}
                    </div>
                  ) : orders.length === 0 && quotes.length === 0 ? (
                    <div className="text-center py-12">
                      <Ship className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">
                        {hasActiveFilters
                          ? 'No hay órdenes Import/Export con los filtros seleccionados'
                          : 'No hay órdenes Import/Export registradas'}
                      </p>
                      {!hasActiveFilters && (
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => setShowCreateDialog(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Registrar Primera Orden
                        </Button>
                      )}
                    </div>
                  ) : viewMode === 'pipeline' ? (
                    /* ── Pipeline View ─── */
                    /* Pasamos orders + quotes para que la columna "Cotización" muestre
                       las cotizaciones de importación (isQuote=true) y no quede vacía.
                       Filtro client-side por ordersSearch porque quotes se carga aparte
                       y no respeta el search del backend. */
                    <ImportPipelineBoard
                      orders={(() => {
                        /*
                          Una cotización convertida en orden viene en las dos listas
                          (`isQuote` es su origen y no cambia): sin esto se dibujaba dos
                          veces, con la misma clave. Por id, gana la de órdenes.
                        */
                        const porId = new Map<string, (typeof orders)[number]>();
                        for (const o of [...quotes, ...orders]) porId.set(o.id, o);
                        const combined = [...porId.values()];
                        const q = ordersSearch.trim().toLowerCase();
                        if (!q) return combined;
                        return combined.filter((o) => {
                          const fields = [
                            o.orderNumber,
                            (o as any).blNumber,
                            (o as any).notes,
                            (o as any).freightProvider?.name,
                            (o as any).customer?.name,
                          ];
                          return fields.some(
                            (f) => typeof f === 'string' && f.toLowerCase().includes(q)
                          );
                        });
                      })()}
                      onOrderClick={(id) => navigate(`${detailBase}/${id}`)}
                    />
                  ) : (
                    /* ── Table View ─── */
                    <>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>N° Orden</TableHead>
                              <TableHead>Tipo</TableHead>
                              <TableHead>Cliente</TableHead>
                              <TableHead className="text-center">Progreso</TableHead>
                              <TableHead>Incoterm</TableHead>
                              <TableHead className="text-right">FOB</TableHead>
                              <TableHead className="text-right">Landed Cost</TableHead>
                              <TableHead>Fecha Orden</TableHead>
                              <TableHead>Est. Llegada</TableHead>
                              <TableHead>Productos</TableHead>
                              <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {orders.map((order, idx) => (
                              <TableRow
                                key={order.id}
                                className="cursor-pointer hover:bg-accent animate-fade-in"
                                style={{
                                  animationDelay: `${idx * 30}ms`,
                                  animationFillMode: 'backwards',
                                }}
                                onClick={() => navigate(`${detailBase}/${order.id}`)}
                              >
                                <TableCell className="font-mono font-semibold">
                                  {order.orderNumber}
                                </TableCell>
                                <TableCell>
                                  <TradeTypeBadge tradeType={order.tradeType} />
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-sm">
                                      {order.customer?.name ?? '—'}
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex justify-center">
                                    <StatusMiniProgress
                                      status={order.status}
                                      showEta
                                      etaDate={order.estimatedArrivalDate}
                                    />
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm">{order.incoterm}</TableCell>
                                <TableCell className="text-right font-semibold tabular-nums">
                                  {formatMoney(order.totalFobValue, order.currency)}
                                </TableCell>
                                <TableCell className="text-right font-semibold tabular-nums text-primary">
                                  {order.totalLandedCost > 0
                                    ? formatMoney(order.totalLandedCost, order.currency)
                                    : '-'}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {formatDate(order.orderDate)}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {formatDate(order.estimatedArrivalDate)}
                                </TableCell>
                                <TableCell className="text-center text-sm">
                                  {order._count.items}
                                </TableCell>
                                <TableCell
                                  className="text-right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex justify-end gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => navigate(`${detailBase}/${order.id}`)}
                                        >
                                          <Eye className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Ver detalle</TooltipContent>
                                    </Tooltip>
                                    {order.status === 'DRAFT' && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDelete(order.id)}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Eliminar</TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Pagination */}
                      {ordersTotalPages > 1 && (
                        <div className="flex items-center justify-between pt-4">
                          <p className="text-sm text-muted-foreground">
                            Página {ordersPage} de {ordersTotalPages} ({ordersTotal} resultados)
                          </p>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={ordersPage <= 1}
                              onClick={() => handlePageChange(ordersPage - 1)}
                            >
                              Anterior
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={ordersPage >= ordersTotalPages}
                              onClick={() => handlePageChange(ordersPage + 1)}
                            >
                              Siguiente
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Cotizaciones ─────────────────
                Lista propia con la vigencia como eje: una cotización vencida no
                se puede prometer porque el tarifario cambia cada semana. */}
            <TabsContent value="quotes">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      {/*
                        19-oct (artefacto): sin título propio —el de la página ya dice
                        «Cotizaciones»— y sin «Ir al Cotizador», que repetía el botón
                        del encabezado.
                      */}
                      {/*
                        Antes decía "14 esperando respuesta del cliente" usando
                        el TOTAL, cuando de esas 14 solo 2 se habían mandado. El
                        número que espera respuesta lo dice el resumen de abajo,
                        que lo sabe de verdad; acá va lo que el título no dice.
                      */}
                      <CardDescription>
                        {quotesTotal > 0
                          ? `${quotesTotal} ${quotesTotal === 1 ? 'cotización' : 'cotizaciones'} ` +
                            (quotesHistory
                              ? 'en total, incluidas las que ya son embarque'
                              : 'en juego')
                          : 'Las cotizaciones que guardes en el Cotizador aparecen acá'}
                      </CardDescription>

                      {/*
                        Ver también las ganadas.

                        El tablero decía "7 de 8 cerradas se ganaron" y esas 7 no
                        estaban en ninguna lista: al convertirse en embarque
                        salieron de `status = QUOTE`. A la pregunta "¿cuáles
                        ganamos?" solo se podía contestar con un número.

                        Va apagado por defecto: el trabajo del día son las que
                        esperan respuesta, no el archivo histórico.
                      */}
                      <label className="mt-1.5 flex w-fit cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={quotesHistory}
                          onChange={(e) => {
                            const history = e.target.checked;
                            setQuotesHistory(history);
                            // Vuelve a la primera página: la página 3 del
                            // listado corto casi nunca existe en el largo.
                            void fetchQuotes({ page: 1, history });
                          }}
                          className="h-3.5 w-3.5 rounded border-border"
                        />
                        {/*
                          Va marcado por defecto: la lista muestra TODO lo
                          cotizado. Desmarcarlo la reduce a lo que espera
                          respuesta, que es lo contrario de lo que decía antes.
                        */}
                        Incluir las que ya son embarque{' '}
                        <span className="text-muted-foreground/70">
                          — acá están las <strong>Aceptadas</strong>
                        </span>
                      </label>
                    </div>
                    {/*
                      Una sola forma de cotizar: el Cotizador.

                      Acá había un segundo botón que abría un asistente para
                      cargar la cotización a mano. Se sacó porque el asistente no
                      pide precios: pregunta cliente, fechas, incoterm y puertos,
                      y deja la cotización en cero. Ofrecer "cotizar" algo que no
                      cotiza nada es peor que no ofrecerlo — quien lo intentaba
                      terminaba con una cotización vacía sin saber por qué.

                      Si hace falta cubrir una ruta que el tarifario no tiene, el
                      camino correcto es cargar esa tarifa en el Tarifario y
                      cotizar normal: así el precio queda respaldado, se puede
                      recotizar y sirve para la próxima.
                    */}
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/*
                    El embudo y la tabla comparten el filtro por estado: tocar
                    "Ganadas" arriba deja abajo solo las ganadas, y volver a
                    tocarla lo quita. Antes las tarjetas eran números sueltos y
                    había que filtrar a mano adivinando la correspondencia.
                  */}
                  <QuoteFunnel
                    refreshKey={quotesVersion}
                    activo={filtroResultado}
                    onSelect={setFiltroResultado}
                  />
                  <QuotesTable
                    quotes={quotes}
                    isLoading={isLoadingQuotes}
                    outcomeFilter={filtroResultado}
                    onOutcomeFilterChange={setFiltroResultado}
                    onOpen={(id) => navigate(`${detailBase}/${id}`)}
                    onConvert={(quote) => handleConvertQuote(quote)}
                    onChanged={() => {
                      void fetchQuotes();
                      setQuotesVersion((v) => v + 1);
                    }}
                    onRequote={(id) => setRequoteId(id)}
                    onDuplicate={(quote) => duplicarCotizacion(quote)}
                  />

                  {/*
                    Paginador. Solo aparece si hay más de una página: con 14
                    cotizaciones, un "1 de 1" con dos flechas apagadas es ruido.

                    Dice el rango y el total —"1–25 de 62"— y no solo el número
                    de página: sin el total, nadie sabe si vale la pena seguir.
                  */}
                  {quotesTotalPages > 1 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                      <p className="text-xs text-muted-foreground">
                        {(quotesPage - 1) * QUOTES_PER_PAGE + 1}–
                        {Math.min(quotesPage * QUOTES_PER_PAGE, quotesTotal)} de {quotesTotal}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={quotesPage <= 1 || isLoadingQuotes}
                          onClick={() => void fetchQuotes({ page: quotesPage - 1 })}
                        >
                          Anterior
                        </Button>
                        <span className="px-1 text-xs tabular-nums text-muted-foreground">
                          {quotesPage} / {quotesTotalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={quotesPage >= quotesTotalPages || isLoadingQuotes}
                          onClick={() => void fetchQuotes({ page: quotesPage + 1 })}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Availability Tab */}
            <TabsContent value="availability">
              <ContainerAvailabilityView />
            </TabsContent>

            {/* KPIs Tab */}
            <TabsContent value="dashboard">
              <ImportDashboardTab />
            </TabsContent>
          </Tabs>
        </FadeIn>
      </div>

      {/* Create order wizard (3 steps: datos / productos / tramos) */}
      <ImportOrderWizard
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={(orderId) => {
          fetchOrders({ stage: 'operational' });
          navigate(`/trade/embarques/${orderId}`);
        }}
      />
      {/*
        Acá vivía un segundo asistente, para cargar una cotización a mano desde
        Cotizaciones. Se quitó junto con el botón que lo abría: quedó sin forma
        de invocarse, y un diálogo que nadie puede abrir es peso muerto que el
        próximo que lea esto va a tener que investigar.
      */}
      <ImportTaxConfigDialog open={showTaxConfigDialog} onOpenChange={setShowTaxConfigDialog} />
      <RequoteDialog
        quoteId={requoteId}
        onClose={() => setRequoteId(null)}
        onContinue={(prefill) => {
          // Se lleva al cotizador con TODO cargado y `desde` para encadenar.
          // Guardar sigue siendo un paso aparte: el precio viejo puede estar ya
          // prometido, y pisarlo sin que nadie lo mire sería peor que el
          // trabajo manual que esto viene a evitar.
          const p = new URLSearchParams();
          p.set('origen', prefill.originLocationId);
          p.set('destino', prefill.destinationLocationId);
          if (prefill.containerTypeId) p.set('equipo', prefill.containerTypeId);
          if (prefill.weightKg !== null) p.set('peso', String(prefill.weightKg));
          p.set('cliente', prefill.customerId ?? '');
          p.set('desde', prefill.quotedFromId);
          setRequoteId(null);
          navigate(`/trade/cotizador?${p.toString()}`);
        }}
      />
      <ConfirmDialog />
    </AppLayout>
  );
}

/** «2026-05» → «may 2026»: la tendencia mensual en palabras. */
function nombreDelMes(aaaaMm: string): string {
  const fecha = new Date(`${aaaaMm}-15T12:00:00Z`);
  if (Number.isNaN(fecha.getTime())) return aaaaMm;
  return fecha.toLocaleDateString('es-BO', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// ── Dashboard Tab (inline) ───────────────────────────────────────

function ImportDashboardTab() {
  const {
    dashboardStats,
    importsByStatus,
    monthlyTrends,
    topSuppliers,
    costVarianceAlerts,
    isLoadingDashboard,
  } = useImportTrackingStore();

  if (isLoadingDashboard) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Extra KPI cards */}
      {dashboardStats && (
        <Card>
          <CardHeader>
            <CardTitle>Tiempos promedio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-3xl font-bold text-primary">
                  {dashboardStats.avgDaysInTransit.toFixed(0)}
                </p>
                <p className="text-sm text-muted-foreground">Días en tránsito</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-3xl font-bold text-alerta">
                  {dashboardStats.avgDaysInCustoms.toFixed(0)}
                </p>
                <p className="text-sm text-muted-foreground">Días en aduana</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/*
        Conversión de cotizaciones.

        Es el indicador que importa para vender: de cada 100 cotizaciones que
        salen, cuántas terminan siendo embarque. Los tiempos promedio miden cómo
        opera la empresa; esto mide si el precio compite.
      */}
      {dashboardStats?.quoteConversion && (
        <Card>
          <CardHeader>
            <CardTitle>Conversión de cotizaciones</CardTitle>
            <CardDescription>Cuántas de las que salieron se volvieron embarque</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardStats.quoteConversion.totalQuoted === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Todavía no se cotizó nada. Andá al Cotizador y guardá la primera.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-lg bg-muted/50 p-4 text-center">
                    <p className="text-3xl font-bold text-primary">
                      {dashboardStats.quoteConversion.ratePercent !== null
                        ? formatoPorcentaje(dashboardStats.quoteConversion.ratePercent, {
                            decimalesMaximos: 1,
                          })
                        : '—'}
                    </p>
                    <p className="text-sm text-muted-foreground">Conversión</p>
                  </div>
                  <div className="rounded-lg bg-muted/50 p-4 text-center">
                    <p className="text-3xl font-bold text-ok">
                      {dashboardStats.quoteConversion.converted}
                    </p>
                    <p className="text-sm text-muted-foreground">Aceptadas</p>
                  </div>
                  <div className="rounded-lg bg-muted/50 p-4 text-center">
                    <p className="text-3xl font-bold text-alerta">
                      {dashboardStats.quoteConversion.pending}
                    </p>
                    <p className="text-sm text-muted-foreground">Esperando respuesta</p>
                  </div>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Sobre {dashboardStats.quoteConversion.totalQuoted} cotizaciones emitidas en total.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Imports by status */}
      <Card>
        <CardHeader>
          <CardTitle>Órdenes por estado</CardTitle>
        </CardHeader>
        <CardContent>
          {importsByStatus.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-sm">Sin datos</p>
          ) : (
            <div className="space-y-2">
              {importsByStatus.map((item) => {
                const total = importsByStatus.reduce((s, i) => s + i.count, 0);
                const pct = total > 0 ? (item.count / total) * 100 : 0;
                return (
                  <div key={item.status} className="flex items-center gap-3">
                    <ImportStatusBadge status={item.status} className="w-28" />
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium tabular-nums w-8 text-right">
                      {item.count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Suppliers */}
      <Card>
        <CardHeader>
          <CardTitle>Los proveedores con más volumen</CardTitle>
        </CardHeader>
        <CardContent>
          {topSuppliers.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-sm">Sin datos</p>
          ) : (
            <div className="space-y-3">
              {topSuppliers.slice(0, 5).map((supplier, idx) => (
                <div key={supplier.supplierId} className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-muted-foreground w-6">
                    #{idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{supplier.supplierName}</p>
                    <p className="text-xs text-muted-foreground">
                      {supplier.orderCount} {supplier.orderCount === 1 ? 'orden' : 'órdenes'}
                    </p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums">
                    {new Intl.NumberFormat('es-BO', {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0,
                    }).format(supplier.totalFob)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly trends */}
      <Card>
        <CardHeader>
          <CardTitle>Tendencia por mes</CardTitle>
        </CardHeader>
        <CardContent>
          {monthlyTrends.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-sm">Sin datos</p>
          ) : (
            <div className="space-y-2">
              {monthlyTrends.slice(-6).map((month) => (
                <div key={month.month} className="flex items-center gap-3 text-sm">
                  <span className="w-20 text-muted-foreground">{nombreDelMes(month.month)}</span>
                  <span className="font-medium">
                    {month.orderCount} {month.orderCount === 1 ? 'orden' : 'órdenes'}
                  </span>
                  <span className="ml-auto tabular-nums text-muted-foreground">
                    FOB: USD {formatoCantidad(month.totalFob)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost Variance Alerts */}
      {costVarianceAlerts.length > 0 && (
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-destructive">
              Órdenes con sobrecosto alto sobre FOB
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {costVarianceAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-3 bg-destructive/5 rounded-lg border border-destructive/20"
                >
                  <div>
                    <p className="text-sm font-medium">
                      {alert.orderNumber} - {alert.concept}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      FOB: USD {formatoPrecio(alert.expectedAmount)} · Nacionalizado: USD{' '}
                      {formatoPrecio(alert.actualAmount)}
                    </p>
                  </div>
                  <span className="text-sm font-bold text-destructive">
                    {formatoPorcentaje(alert.variancePercent, { signo: true })}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ImportOrdersPage;
